import { useMemo } from 'react';
import { useTenant } from '../contexts/TenantContext';

function groupIndian(intPart) {
  if (intPart.length <= 3) return intPart;
  const last3 = intPart.slice(-3);
  const rest = intPart.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

function groupWestern(intPart) {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function makeFormatter(symbol, currencyCode) {
  return function formatMoney(amount, opts) {
    const n = Number(amount ?? 0);
    if (!Number.isFinite(n)) return `${symbol}0`;
    const decimals = opts?.decimals ?? 0;
    const negative = n < 0;
    const abs = Math.abs(n);
    const fixed = abs.toFixed(decimals);
    const [intPart, decPart] = fixed.split('.');
    const grouped = currencyCode === 'INR' ? groupIndian(intPart) : groupWestern(intPart);
    const numberStr = decPart ? `${grouped}.${decPart}` : grouped;
    return `${negative ? '-' : ''}${symbol}${numberStr}`;
  };
}

const DEFAULTS = {
  currencyCode: 'INR',
  currencySymbol: '₹',
  taxLabel: 'GST',
  taxIdLabel: 'GSTIN',
  taxRateDefault: 18,
  countryCode: 'IN',
  formatMoney: makeFormatter('₹', 'INR'),
};

export function useTenantFormat() {
  const { region } = useTenant();
  return useMemo(() => {
    if (!region) return DEFAULTS;
    const currencyCode = region.currency_code || 'INR';
    const currencySymbol = region.currency_symbol || '₹';
    const taxLabel = region.tax_label === 'VAT' ? 'VAT' : 'GST';
    const taxIdLabel = region.tax_id_label === 'TRN' ? 'TRN' : 'GSTIN';
    return {
      currencyCode,
      currencySymbol,
      taxLabel,
      taxIdLabel,
      taxRateDefault: Number(region.tax_rate_default ?? 18),
      countryCode: region.country_code || 'IN',
      formatMoney: makeFormatter(currencySymbol, currencyCode),
    };
  }, [region]);
}

/**
 * For historical records that carry snapshot currency fields.
 * Falls back to current tenant format if snapshot is missing.
 */
export function formatSnapshotMoney(amount, snapshotSymbol, snapshotCode, fallbackFmt) {
  if (snapshotSymbol && snapshotCode) {
    return makeFormatter(snapshotSymbol, snapshotCode)(amount);
  }
  return fallbackFmt.formatMoney(amount);
}
